import { useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { isGoogleSignInConfigured, mountGoogleButton } from "@/services/socialAuth";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSubmit: (data: LoginFormData) => Promise<void>;
  onGoogleSuccess?: (result: import("@/services/socialAuth").SocialAuthResult) => Promise<void>;
  error?: Error | null;
  isLoading?: boolean;
  isGoogleLoading?: boolean;
}

export default function LoginForm({ onSubmit, onGoogleSuccess, error, isLoading, isGoogleLoading }: LoginFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const googleBtnRef = useRef<HTMLDivElement>(null);
  const showGoogleLogin = isGoogleSignInConfigured() && !!onGoogleSuccess;

  useEffect(() => {
    if (!showGoogleLogin || !googleBtnRef.current || !onGoogleSuccess) return;

    // Render the GIS button and wire up the success handler
    mountGoogleButton(googleBtnRef.current, onGoogleSuccess);
  // Re-render button whenever the container mounts or onGoogleSuccess changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGoogleLogin]);

  const handleFormSubmit = async (data: LoginFormData) => {
    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-5">
      {error && (
        <div className="rounded-lg bg-danger-50 border border-danger-200 p-4">
          <p className="text-sm text-danger-700">
            {error.message.includes('cancelled') || error.message.includes('Redirecting')
              ? 'Sign-in was cancelled. Please try again.'
              : error.message || 'Sign-in failed. Please try again.'}
          </p>
        </div>
      )}

      {showGoogleLogin && (
        <>
          <div className="relative">
            {/* GIS renders its own button into this div */}
            <div
              ref={googleBtnRef}
              className="w-full flex items-center justify-center"
              style={{ minHeight: 44 }}
            />
            {isGoogleLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg">
                <svg className="animate-spin h-5 w-5 text-primary-600" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            )}
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-2 text-gray-500">Or continue with email</span>
            </div>
          </div>
        </>
      )}

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email address
        </label>
        <input
          {...register("email")}
          type="email"
          id="email"
          autoComplete="email"
          className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none transition-colors"
          placeholder="you@example.com"
        />
        {errors.email && (
          <p className="mt-1 text-sm text-danger-600">{errors.email.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
          Password
        </label>
        <input
          {...register("password")}
          type="password"
          id="password"
          autoComplete="current-password"
          className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none transition-colors"
          placeholder="Enter your password"
        />
        {errors.password && (
          <p className="mt-1 text-sm text-danger-600">{errors.password.message}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={isLoading || isGoogleLoading}
        className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? (
          <span className="flex items-center justify-center space-x-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span>Signing in...</span>
          </span>
        ) : (
          "Sign in"
        )}
      </button>

      <p className="text-center text-sm text-gray-500">
        Don&apos;t have an account?{" "}
        <Link to="/register" className="font-medium text-primary-600 hover:text-primary-500">
          Register here
        </Link>
      </p>
    </form>
  );
}
